import React, { useContext, useReducer, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FileSpreadsheet, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import reservaService from '../../services/reserva.service';
import cuentaBancariaService, { cuentaLabel } from '../../services/cuentaBancaria.service';
import rifaService from '../../services/rifa.service';
import { Store } from '../../Store';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { getError } from '../../utils';
import LoadingBox from '../../components/LoadingBox';
import StatusLoadingOverlay from '../../components/StatusLoadingOverlay';
import ReservaTable from '../../components/ReservaTable';
import ConfirmModal from '../../components/ConfirmModal';
import BoardCasillaRepositionModal from '../../components/BoardCasillaRepositionModal';

const TICKET_PDF_STATUS_KEYS = [
  'reservation.ticketDownloadPreparing',
  'reservation.ticketDownloadGenerating',
  'reservation.ticketDownloadRendering',
  'reservation.ticketDownloadDownloading',
];

const TICKET_ZIP_STATUS_KEYS = [
  'reservation.ticketDownloadPreparing',
  'reservation.ticketDownloadGeneratingMany',
  'reservation.ticketDownloadRendering',
  'reservation.ticketDownloadPacking',
  'reservation.ticketDownloadDownloading',
];

const EXPORT_STATUS_KEYS = [
  'reservation.exportPreparing',
  'reservation.exportGenerating',
  'reservation.exportDownloading',
];

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_PENDIENTES_SUCCESS':
      return {
        ...state,
        reservas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        loading: false,
        pages: action.payload?.totalAmount ?? 0,
      };
    case 'FETCH_PENDIENTESDEVERIFICACION_SUCCESS':
      return {
        ...state,
        reservasParaVerificar: Array.isArray(action.payload?.reservas)
          ? action.payload.reservas
          : [],
        loading: false,
        paraVerificarQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'FETCH_VERIFICADAS_SUCCESS':
      return {
        ...state,
        reservasVerificadas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        loading: false,
        verificadasQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'FETCH_CANCELADAS_SUCCESS':
      return {
        ...state,
        reservasCanceladas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        loading: false,
        canceladasQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'SET_PENDIENTES_LIST':
      return {
        ...state,
        reservas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        pages: action.payload?.totalAmount ?? 0,
      };
    case 'SET_PARA_VERIFICAR_LIST':
      return {
        ...state,
        reservasParaVerificar: Array.isArray(action.payload?.reservas)
          ? action.payload.reservas
          : [],
        paraVerificarQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'SET_VERIFICADAS_LIST':
      return {
        ...state,
        reservasVerificadas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        verificadasQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'SET_CANCELADAS_LIST':
      return {
        ...state,
        reservasCanceladas: Array.isArray(action.payload?.reservas) ? action.payload.reservas : [],
        canceladasQuantity: action.payload?.totalAmount ?? 0,
      };
    case 'FETCH_FAIL':
      return { ...state, loadingRifas: false, error: action.payload };
    case 'FETCH_RIFAS_REQUEST':
      return { ...state, loadingRifas: true };
    case 'FETCH_RIFAS_SUCCESS':
      return {
        ...state,
        rifas: Array.isArray(action.payload) ? action.payload : [],
        loadingRifas: false,
        error: '',
      };
    case 'FETCH_RIFAS_FAIL':
      return { ...state, loadingRifas: false, errorRifas: action.payload, error: action.payload };
    default:
      return state;
  }
};

const STATUS_TABS = ['Pendiente', 'Confirmada', 'Verificada', 'Cancelada'];

const STATUS_TAB_LABELS = {
  Pendiente: 'reservation.pending',
  Confirmada: 'reservation.confirmed',
  Verificada: 'reservation.verified',
  Cancelada: 'reservation.cancelled',
};

export default function Reservas() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [
    {
      loadingRifas,
      error,
      reservas,
      reservasParaVerificar,
      reservasVerificadas,
      reservasCanceladas,
      pages,
      paraVerificarQuantity,
      verificadasQuantity,
      canceladasQuantity,
      rifas,
    },
    dispatch,
  ] = useReducer(reducer, {
    loadingRifas: true,
    error: '',
    reservas: [],
    reservasParaVerificar: [],
    reservasVerificadas: [],
    reservasCanceladas: [],
    pages: 1,
    paraVerificarQuantity: 0,
    verificadasQuantity: 0,
    canceladasQuantity: 0,
    rifas: [],
  });

  const [selectedRifaId, setSelectedRifaId] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservaToCancel, setReservaToCancel] = useState(null);
  const [activeStatus, setActiveStatus] = useState('Pendiente');
  const [tabCounts, setTabCounts] = useState({
    Pendiente: 0,
    Confirmada: 0,
    Verificada: 0,
    Cancelada: 0,
  });
  const [loadingTab, setLoadingTab] = useState(false);
  const [showRepositionModal, setShowRepositionModal] = useState(false);
  const [statusOverlayKeys, setStatusOverlayKeys] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  /** '' = all, account id, or 'none' (reservations without account) */
  const [cuentaFilter, setCuentaFilter] = useState('');
  const itemsPerPage = 15;

  const [currentPageP, setCurrentPageP] = useState(1);
  const [currentPagePV, setCurrentPagePV] = useState(1);
  const [currentPageV, setCurrentPageV] = useState(1);
  const [currentPageC, setCurrentPageC] = useState(1);

  const activePage = useMemo(() => {
    switch (activeStatus) {
      case 'Pendiente':
        return currentPageP;
      case 'Confirmada':
        return currentPagePV;
      case 'Verificada':
        return currentPageV;
      case 'Cancelada':
        return currentPageC;
      default:
        return 1;
    }
  }, [activeStatus, currentPageP, currentPagePV, currentPageV, currentPageC]);

  const selectedRifa = useMemo(
    () => rifas.find(r => r._id === selectedRifaId),
    [rifas, selectedRifaId]
  );

  const fetchTabData = useCallback(
    async (status, page) => {
      if (!selectedRifaId || !status) return;
      const skip = (page - 1) * itemsPerPage;
      setLoadingTab(true);
      try {
        const data = await reservaService.getReservasByStatus(
          selectedRifaId,
          status,
          skip,
          itemsPerPage,
          cuentaFilter
        );
        const actionByStatus = {
          Pendiente: 'SET_PENDIENTES_LIST',
          Confirmada: 'SET_PARA_VERIFICAR_LIST',
          Verificada: 'SET_VERIFICADAS_LIST',
          Cancelada: 'SET_CANCELADAS_LIST',
        };
        dispatch({ type: actionByStatus[status], payload: data });
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setLoadingTab(false);
      }
    },
    [selectedRifaId, itemsPerPage, cuentaFilter]
  );

  const reloadTabCounts = useCallback(async () => {
    if (!selectedRifaId) return;
    const statuses = ['Pendiente', 'Confirmada', 'Verificada', 'Cancelada'];
    const results = await Promise.all(
      statuses.map(s => reservaService.getReservasByStatus(selectedRifaId, s, 0, 1, cuentaFilter))
    );
    setTabCounts({
      Pendiente: results[0].totalAmount ?? 0,
      Confirmada: results[1].totalAmount ?? 0,
      Verificada: results[2].totalAmount ?? 0,
      Cancelada: results[3].totalAmount ?? 0,
    });
  }, [selectedRifaId, cuentaFilter]);

  const refreshAfterMutation = useCallback(async () => {
    await reloadTabCounts();
    await fetchTabData(activeStatus, activePage);
  }, [reloadTabCounts, fetchTabData, activeStatus, activePage]);

  const fetchRifas = async () => {
    try {
      dispatch({ type: 'FETCH_RIFAS_REQUEST' });
      const raw = await rifaService.getRifas(0, 100);
      const data = Array.isArray(raw) ? raw : Array.isArray(raw?.rifas) ? raw.rifas : [];
      dispatch({ type: 'FETCH_RIFAS_SUCCESS', payload: data });
      if (data.length > 0) setSelectedRifaId(data[0]._id);
    } catch (err) {
      dispatch({ type: 'FETCH_RIFAS_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    fetchRifas();
  }, []);

  useEffect(() => {
    if (!userInfo?.isAdmin) return;
    cuentaBancariaService
      .getAll()
      .then(setCuentas)
      .catch(() => {});
  }, [userInfo?.isAdmin]);

  const resetPages = () => {
    setCurrentPageP(1);
    setCurrentPagePV(1);
    setCurrentPageV(1);
    setCurrentPageC(1);
  };

  const changeReservaCuenta = async (reserva, cuentaId) => {
    try {
      await cuentaBancariaService.setReservaCuenta(reserva._id, cuentaId);
      toast.success('Destino actualizado');
      await refreshAfterMutation();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  useEffect(() => {
    if (!selectedRifaId) return;
    let cancelled = false;
    (async () => {
      try {
        const statuses = ['Pendiente', 'Confirmada', 'Verificada', 'Cancelada'];
        const results = await Promise.all(
          statuses.map(s =>
            reservaService.getReservasByStatus(selectedRifaId, s, 0, 1, cuentaFilter)
          )
        );
        if (!cancelled) {
          setTabCounts({
            Pendiente: results[0].totalAmount ?? 0,
            Confirmada: results[1].totalAmount ?? 0,
            Verificada: results[2].totalAmount ?? 0,
            Cancelada: results[3].totalAmount ?? 0,
          });
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRifaId, cuentaFilter]);

  useEffect(() => {
    if (!selectedRifaId) return;
    fetchTabData(activeStatus, activePage);
  }, [selectedRifaId, activeStatus, activePage, fetchTabData]);

  // — reservation actions —
  const confirmarPago = reserva => navigate(`/AdminScreen/confirmarPago/${reserva._id}`);

  const verificarPago = async reserva => {
    try {
      await reservaService.verificarReserva(reserva._id);
      toast.success('Pago Verificado');
      await refreshAfterMutation();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  /**
   * How the server splits this reservation's PDFs: letters ("carta" format) are one per board
   * square (or one per reservation); tickets are one per number (or per square without numbers).
   */
  const pdfPlan = reserva => {
    const isCarta = reserva.rifa?.pdfLayout?.format === 'carta';
    const squares = reserva.casillas?.length || 0;
    const numbers = reserva.tickets?.length || 0;
    const count = isCarta ? Math.max(1, squares) : numbers || Math.max(1, squares);
    const hidden = reserva.rifa?.mostrarNumeros === false;
    const single = isCarta
      ? 'Carta.pdf'
      : hidden || !numbers
        ? 'Bono.pdf'
        : `Boleto #${reserva.tickets[0]}.pdf`;
    return { isZip: count > 1, zipName: isCarta ? 'Cartas.zip' : 'Boletos.zip', single };
  };

  const downloadFromServer = async reserva => {
    const plan = pdfPlan(reserva);
    if (plan.isZip) {
      const data = await reservaService.descargarBoletoReservaZip(reserva._id);
      return { blob: new Blob([data], { type: 'application/zip' }), fileName: plan.zipName };
    }
    const data = await reservaService.descargarBoletoReserva(reserva._id);
    return {
      blob: new Blob([data], { type: 'application/pdf' }),
      fileName: plan.single,
    };
  };

  const descargarBoleto = async reserva => {
    const { isZip } = pdfPlan(reserva);
    setStatusOverlayKeys(isZip ? TICKET_ZIP_STATUS_KEYS : TICKET_PDF_STATUS_KEYS);
    try {
      const { blob, fileName } = await downloadFromServer(reserva);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setStatusOverlayKeys(null);
    }
  };

  const verComprobante = reserva => {
    if (reserva.comprobante)
      window.open(reserva.comprobante.fileLink, '_blank', 'noopener,noreferrer');
  };

  const downloadExcel = async () => {
    setStatusOverlayKeys(EXPORT_STATUS_KEYS);
    try {
      const data = await reservaService.exportToExcel(selectedRifaId);
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Reservas.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setStatusOverlayKeys(null);
    }
  };

  const handleRifaChange = e => {
    const id = e.target.value;
    setCurrentPageP(1);
    setCurrentPagePV(1);
    setCurrentPageV(1);
    setCurrentPageC(1);
    setActiveStatus('Pendiente');
    setSelectedRifaId(id);
  };

  // —— active reservas for current tab ——
  const activeReservas = {
    Pendiente: { data: reservas, total: pages, page: currentPageP, onPage: setCurrentPageP },
    Confirmada: {
      data: reservasParaVerificar,
      total: paraVerificarQuantity,
      page: currentPagePV,
      onPage: setCurrentPagePV,
    },
    Verificada: {
      data: reservasVerificadas,
      total: verificadasQuantity,
      page: currentPageV,
      onPage: setCurrentPageV,
    },
    Cancelada: {
      data: reservasCanceladas,
      total: canceladasQuantity,
      page: currentPageC,
      onPage: setCurrentPageC,
    },
  };

  if (loadingRifas) return <LoadingBox overlay={true} />;
  if (error) return <p className='px-6 py-5 text-sm text-destructive'>{error}</p>;

  const current = activeReservas[activeStatus];

  return (
    <div className='space-y-6'>
      {statusOverlayKeys && <StatusLoadingOverlay messageKeys={statusOverlayKeys} />}
      <BoardCasillaRepositionModal
        show={showRepositionModal}
        onHide={() => setShowRepositionModal(false)}
        rifaId={selectedRifaId}
        onAfterSwap={refreshAfterMutation}
      />

      <ConfirmModal
        show={showCancelModal}
        title={t('reservation.cancelReservation')}
        message={t('reservation.confirmCancel')}
        onConfirm={async () => {
          try {
            await reservaService.cancelarReserva(reservaToCancel._id);
            toast.success(t('reservation.cancelSuccess') || 'Reserva cancelada');
            setShowCancelModal(false);
            setReservaToCancel(null);
            await refreshAfterMutation();
          } catch (err) {
            toast.error(getError(err));
          }
        }}
        onCancel={() => setShowCancelModal(false)}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />

      <ConfirmModal
        show={showDeleteModal}
        title={t('reservation.deleteReservation')}
        message={t('reservation.confirmDelete')}
        onConfirm={() => setShowDeleteModal(false)}
        onCancel={() => setShowDeleteModal(false)}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
      />

      <PageHeader
        title={t('reservation.reservationList')}
        subtitle='Gestioná las reservas de todos tus bonos'
        actions={
          <div className='flex items-center gap-2'>
            {userInfo?.isAdmin && selectedRifa?.tipo === 'board' && (
              <Button
                variant='outline'
                onClick={() => setShowRepositionModal(true)}
                disabled={!selectedRifaId}
              >
                <ArrowLeftRight className='h-4 w-4' />
                {t('reservation.repositionShort') || 'Reposicionar'}
              </Button>
            )}
            <Button variant='outline' onClick={downloadExcel} disabled={!selectedRifaId}>
              <FileSpreadsheet className='h-4 w-4' />
              {t('common.download')}
            </Button>
          </div>
        }
      />

      {/* Rifa selector */}
      <div className='flex flex-wrap items-center gap-3'>
        <label className='text-sm font-medium text-foreground' htmlFor='rifa-select-res'>
          {t('reservation.chooseRaffle') || 'Bono'}
        </label>
        <div className='relative'>
          <select
            id='rifa-select-res'
            value={selectedRifaId}
            onChange={handleRifaChange}
            disabled={rifas.length === 0}
            className='h-9 appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
          >
            <option value=''>{t('reservation.chooseRaffle')}</option>
            {rifas.map(rifa => (
              <option key={rifa._id} value={rifa._id}>
                {rifa.nombre}
              </option>
            ))}
          </select>
        </div>
        {rifas.length === 0 && (
          <p className='text-sm text-muted-foreground'>{t('reservation.noRafflesForList')}</p>
        )}
        {cuentas.length > 0 && (
          <>
            <label className='text-sm font-medium text-foreground' htmlFor='cuenta-filter-res'>
              Destino
            </label>
            <select
              id='cuenta-filter-res'
              value={cuentaFilter}
              onChange={e => {
                resetPages();
                setCuentaFilter(e.target.value);
              }}
              className='h-9 max-w-[220px] appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
            >
              <option value=''>Todos</option>
              {cuentas.map(c => (
                <option key={c._id} value={c._id}>
                  {cuentaLabel(c)}
                </option>
              ))}
              <option value='none'>Sin destino</option>
            </select>
          </>
        )}
      </div>

      {/* Status tabs */}
      {selectedRifaId && (
        <>
          <div className='flex border-b border-border'>
            {STATUS_TABS.map(status => (
              <button
                key={status}
                type='button'
                onClick={() => setActiveStatus(status)}
                className={cn(
                  '-mb-px px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none',
                  activeStatus === status
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {t(STATUS_TAB_LABELS[status])} ({tabCounts[status]})
              </button>
            ))}
          </div>

          <div className='relative' style={{ minHeight: 180 }}>
            {loadingTab && (
              <div className='absolute inset-0 z-10 flex items-center justify-center rounded bg-background/75'>
                <div className='h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent' />
              </div>
            )}

            <ReservaTable
              title={t(STATUS_TAB_LABELS[activeStatus])}
              reservas={current.data}
              totalCount={current.total}
              currentPage={current.page}
              onPageChange={current.onPage}
              itemsPerPage={itemsPerPage}
              userInfo={userInfo}
              onConfirmarPago={confirmarPago}
              onDeleteReserva={_r => setShowDeleteModal(true)}
              onCancelarReserva={reserva => {
                setReservaToCancel(reserva);
                setShowCancelModal(true);
              }}
              onVerificarPago={verificarPago}
              onDescargarBoleto={descargarBoleto}
              onVerComprobante={verComprobante}
              showBoardColumns={selectedRifa?.tipo === 'board'}
              cuentas={cuentas}
              onChangeCuenta={changeReservaCuenta}
              t={t}
            />
          </div>
        </>
      )}
    </div>
  );
}
